import { useMemo, useState } from 'react';
import Image from 'next/image';

import { Box, Modal, TextButton } from '@zillow/constellation-10';
import { IconArrowRightFilled } from '@zillow/constellation-icons';

import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { useRentalManagerUserProfileWebApiHandler_saveUserInfo } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { useRentalManagerZillowAccountDetailsV2WebApiHandler_getZillowAccountDetails } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { useRentalManagerUserTrackingWebApiHandler_event } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import typeCheckUtils from '../../../../srcLegacy/shared/utils/typeCheckUtils';
import { usePrefillUserData } from './hooks/usePrefillUserData';
import { usePropertyFlow } from './hooks/usePropertyFlow';
import { useStepHandlers } from './hooks/useStepHandlers';

import { MAX_NUM_UNITS_ERROR, MAX_NUM_UNITS_VALUE, NUMBER_OF_UNITS_ERROR } from '../AddPropertyModal/constants';

import { OnboardingButtons } from './components/OnboardingButtons';
import {
  NameFields,
  ProfileTypeSelection,
  PropertyConfirmation,
  PropertyCountRangeSelect,
  PropertyDetails,
  ThanksPage,
} from './components/QuestionFields';

import ZillowRentalsLogo from '../../../assets/zillow-rentals-logo.svg';

import {
  emitAddressEntryGoBackEvent,
  emitAddressVerificationGoBackEvent,
  emitSkipOnboardingSetupEvent,
  SKIP_ONBOARDING_SETUP_EVENT_ID,
} from '../../../analytics/events/userOnboarding';
import { useAuth } from '../../AuthProvider';
import { STEPS } from './types';

import type { ChangeEvent, FC } from 'react';
import type { ModalRootPropsInterface } from '@zillow/constellation-10';
import type { ListingReducerProps } from '../AddPropertyModal/reducers';
import type { StepType } from './types';

type UserOnboarding = {
  onClose: () => void;
  size?:
    | ModalRootPropsInterface['size']
    | { base?: ModalRootPropsInterface['size']; md?: ModalRootPropsInterface['size'] };
  isNewUser?: boolean;
};

const ONBOARDING_LOGO_WIDTH_PX = 175;
const ONBOARDING_LOGO_HEIGHT_PX = (ONBOARDING_LOGO_WIDTH_PX * 25) / 215;

// Group steps that represent the same "question" so PROPERTY_DETAILS and
// PROPERTY_CONFIRMATION (two screens, one question) share a number.
const STEP_QUESTION_GROUP: Record<StepType, string | null> = {
  [STEPS.NAME]: 'name',
  [STEPS.PROFILE_TYPE]: 'profile-type',
  [STEPS.PROPERTY_COUNT]: 'property-count',
  [STEPS.PROPERTY_DETAILS]: 'property',
  [STEPS.PROPERTY_CONFIRMATION]: 'property',
  [STEPS.THANKS]: null,
};

const UserOnboarding: FC<UserOnboarding> = ({ onClose, size = 'fullScreen', isNewUser = true }) => {
  const { user } = useAuth();

  const { mutate: trackEventMutation } = useRentalManagerUserTrackingWebApiHandler_event();

  const handleClose = () => {
    trackEventMutation({
      getParams: { trackingEvent: RentalManagerUserTrackingEvent.COMPLETE_ON_BOARDING_FLOW },
    });
    onClose();
  };

  const { mutate: saveUserInfo, isPending: isSaving } = useRentalManagerUserProfileWebApiHandler_saveUserInfo();
  const { data: accountDetails } = useRentalManagerZillowAccountDetailsV2WebApiHandler_getZillowAccountDetails({
    enabled: !user?.firstName?.trim() || !user?.lastName?.trim() || !user?.profiles?.profileType,
    meta: {
      tags: ['zillow-account-details'],
      context: {
        api: 'rental-manager',
        details: 'RentalManagerZillowAccountDetailsV2WebApiHandler',
      },
    },
  });

  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    selectedProfileType,
    setSelectedProfileType,
    propertyCountRange,
    setPropertyCountRange,
    stepSkipFlags,
  } = usePrefillUserData(user ?? undefined, accountDetails);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasSavedData, setHasSavedData] = useState(false);
  const [skippedPropertyDetails, setSkippedPropertyDetails] = useState(false);

  const activeSteps = useMemo(() => {
    const steps: Array<StepType> = [];

    if (!stepSkipFlags.skipName) {
      steps.push(STEPS.NAME);
    }

    if (!stepSkipFlags.skipProfileType) {
      steps.push(STEPS.PROFILE_TYPE);
    }

    if (!stepSkipFlags.skipPropertyCount) {
      steps.push(STEPS.PROPERTY_COUNT);
    }

    if (isNewUser) {
      steps.push(STEPS.PROPERTY_DETAILS);
      if (!skippedPropertyDetails) {
        steps.push(STEPS.PROPERTY_CONFIRMATION);
      }
    }

    if (hasSavedData && (!isNewUser || skippedPropertyDetails)) {
      steps.push(STEPS.THANKS);
    }

    return steps;
  }, [isNewUser, hasSavedData, skippedPropertyDetails, stepSkipFlags]);

  const {
    listing,
    setListing,
    listingErrors,
    setListingErrors,
    isSubmitDisabled,
    setIsSubmitDisabled,
    unitNumberDetected,
    setUnitNumberDetected,
    isValidatedWithoutUnitDetected,
    setIsValidatedWithoutUnitDetected,
    propertyAlreadyPostedWarningMessage,
    setPropertyAlreadyPostedWarningMessage,
    isRefocusingAddressInput,
    setIsRefocusingAddressInput,
    isImpreciseLocation,
    contractsResponse,
    verifyCanSubmitForm,
    handleCheckLocationRestriction,
    createProperty,
  } = usePropertyFlow(user ?? undefined);

  const { handleSkip, handleProfileStep, handlePropertyDetailsStep, handlePropertyConfirmationStep } = useStepHandlers({
    user: user ?? null,
    firstName,
    lastName,
    selectedProfileType,
    propertyCountRange,
    saveUserInfo,
    listing,
    setListingErrors,
    verifyCanSubmitForm,
    createProperty,
    setIsSubmitDisabled,
    setUnitNumberDetected,
    setIsValidatedWithoutUnitDetected,
    setPropertyAlreadyPostedWarningMessage,
    setCurrentStepIndex,
    unitNumberDetected,
    isValidatedWithoutUnitDetected,
    propertyAlreadyPostedWarningMessage,
    onSaveSuccess: () => {
      setHasSavedData(true);
      setCurrentStepIndex((i) => i + 1);
    },
    onClose: handleClose,
  });

  const currentStep = activeSteps[currentStepIndex];
  const questionGroups = useMemo(() => {
    const groups: Array<string> = [];
    for (const step of activeSteps) {
      const group = STEP_QUESTION_GROUP[step];
      if (group && !groups.includes(group)) {
        groups.push(group);
      }
    }
    return groups;
  }, [activeSteps]);
  const questionTotal = questionGroups.length;
  const currentGroup = currentStep ? STEP_QUESTION_GROUP[currentStep] : null;
  const questionNumber = currentGroup ? questionGroups.indexOf(currentGroup) + 1 : 0;

  const hasNothingToSave =
    (currentStep === STEPS.NAME && (!firstName.trim() || !lastName.trim())) ||
    (currentStep === STEPS.PROFILE_TYPE && selectedProfileType === undefined) ||
    (currentStep === STEPS.PROPERTY_COUNT && propertyCountRange === undefined);

  const advance = () => {
    if (currentStepIndex >= activeSteps.length - 1) {
      handleClose();
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  };

  const onSkipClick = () => {
    if (currentStep === STEPS.PROPERTY_DETAILS) {
      // If the user saved something earlier in the flow, end on the thanks page;
      // otherwise nothing was entered, so just close (no empty thanks screen).
      if (hasSavedData) {
        setSkippedPropertyDetails(true);
        setCurrentStepIndex((i) => i + 1);
      } else {
        handleClose();
      }
      return;
    }
    handleSkip(currentStep, advance);
  };

  const handleContinue = () => {
    if (!currentStep) {
      handleClose();
      return;
    }

    if (currentStep === STEPS.THANKS) {
      advance();
      return;
    }

    if (currentStep === STEPS.NAME || currentStep === STEPS.PROFILE_TYPE || currentStep === STEPS.PROPERTY_COUNT) {
      handleProfileStep(currentStep, advance);
      return;
    }

    if (currentStep === STEPS.PROPERTY_DETAILS) {
      handlePropertyDetailsStep(advance);
      return;
    }

    if (currentStep === STEPS.PROPERTY_CONFIRMATION) {
      handlePropertyConfirmationStep();
      return;
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      if (currentStep === STEPS.PROPERTY_DETAILS) {
        emitAddressEntryGoBackEvent({ user: user ?? null });
      } else if (currentStep === STEPS.PROPERTY_CONFIRMATION) {
        emitAddressVerificationGoBackEvent({ user: user ?? null });
        setIsRefocusingAddressInput(true);
      }
      setCurrentStepIndex((i) => i - 1);
    }
  };

  const renderQuestionField = () => {
    switch (currentStep) {
      case STEPS.NAME:
        return (
          <NameFields
            firstName={firstName}
            lastName={lastName}
            onFirstNameChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
            onLastNameChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
            questionNumber={questionNumber}
            questionTotal={questionTotal}
          />
        );
      case STEPS.PROFILE_TYPE:
        return (
          <ProfileTypeSelection
            selectedProfileType={selectedProfileType}
            onSelect={setSelectedProfileType}
            questionNumber={questionNumber}
            questionTotal={questionTotal}
            layout={isNewUser ? 'stack' : 'grid'}
          />
        );
      case STEPS.PROPERTY_COUNT:
        return (
          <PropertyCountRangeSelect
            value={propertyCountRange}
            onChange={setPropertyCountRange}
            questionNumber={questionNumber}
            questionTotal={questionTotal}
          />
        );
      case STEPS.PROPERTY_DETAILS:
        return (
          <PropertyDetails
            listing={listing}
            listingErrors={listingErrors}
            contractsResponse={contractsResponse}
            onListingChange={(keyName: keyof ListingReducerProps, newValue: string | boolean) => {
              // Convert boolean to string for legacy reducer
              const stringValue = typeof newValue === 'boolean' ? String(newValue) : newValue;

              const newListing = {
                ...listing,
                [keyName]: stringValue,
              };

              setListingErrors({ type: keyName, payload: '' });
              // if the unit was changed, the entire address will be revalidated
              keyName === 'unit' && setListingErrors({ type: 'address', payload: '' });

              if (
                newListing.numUnits &&
                (!typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) ||
                  String(newListing.numUnits).includes('.'))
              ) {
                setListingErrors({ type: 'numUnits', payload: NUMBER_OF_UNITS_ERROR });
              } else if (
                typeCheckUtils.isNumeric(newListing.numUnits as string | number | undefined) &&
                Number(newListing.numUnits) > MAX_NUM_UNITS_VALUE
              ) {
                setListingErrors({ type: 'numUnits', payload: MAX_NUM_UNITS_ERROR });
              }

              setListing({ type: keyName, payload: keyName === 'numUnits' ? stringValue?.trim() : stringValue });
            }}
            onCheckLocationRestriction={async (
              fieldName?: keyof ListingReducerProps,
              fieldValue?: boolean | string,
            ) => {
              return handleCheckLocationRestriction(
                (newPropertyType: string) => {
                  setListing({ type: 'propertyType', payload: newPropertyType });
                  setListingErrors({ type: 'propertyType', payload: '' });
                },
                fieldName,
                fieldValue,
              );
            }}
            unitNumberDetected={unitNumberDetected}
            isValidatedWithoutUnitDetected={isValidatedWithoutUnitDetected}
            propertyAlreadyPostedWarningMessage={propertyAlreadyPostedWarningMessage}
            isSubmitDisabled={isSubmitDisabled}
            setIsSubmitDisabled={setIsSubmitDisabled}
            isRefocusingAddressInput={isRefocusingAddressInput}
            setIsRefocusingAddressInput={setIsRefocusingAddressInput}
            questionNumber={questionNumber}
            questionTotal={questionTotal}
          />
        );
      case STEPS.PROPERTY_CONFIRMATION:
        return (
          <PropertyConfirmation
            address={listing.address || ''}
            isRoomForRent={
              typeof listing.isRoomForRent === 'boolean' ? listing.isRoomForRent : listing.isRoomForRent === 'true'
            }
            numUnits={listing.numUnits}
            propertyType={listing.propertyType || ''}
            unit={listing.unit}
            user={user || undefined}
            showImpreciseLocationAlert={isImpreciseLocation}
            questionNumber={questionNumber}
            questionTotal={questionTotal}
          />
        );
      case STEPS.THANKS:
        return <ThanksPage />;
      default:
        return null;
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && handleClose()}
      size={size as ModalRootPropsInterface['size']}
      scrollDocument={true}
      dividers={false}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content
            css={
              isNewUser
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                  }
                : {}
            }
          >
            {isNewUser ? (
              <Modal.Header
                css={{
                  display: 'flex',
                  justifyContent: isNewUser ? 'space-between' : 'flex-end',
                  alignItems: 'center',
                }}
              >
                <Image
                  src={ZillowRentalsLogo}
                  alt="Zillow Rentals"
                  width={ONBOARDING_LOGO_WIDTH_PX}
                  height={ONBOARDING_LOGO_HEIGHT_PX}
                  priority
                  style={{ display: 'block', verticalAlign: 'middle' }}
                />

                <Modal.Close data-testid={isNewUser ? 'WizardUserOnboarding-Exit' : 'ExistingUserOnboarding-Exit'}>
                  <TextButton
                    icon={<IconArrowRightFilled />}
                    iconPosition="end"
                    tone="neutral"
                    onClick={() => emitSkipOnboardingSetupEvent({ user: user ?? null })}
                    data-eventid={SKIP_ONBOARDING_SETUP_EVENT_ID}
                  >
                    Skip setup
                  </TextButton>
                </Modal.Close>
              </Modal.Header>
            ) : (
              <Modal.CloseButton
                data-testid={isNewUser ? 'WizardUserOnboarding-Exit' : 'ExistingUserOnboarding-Exit'}
                onClick={() => emitSkipOnboardingSetupEvent({ user: user ?? null })}
                data-eventid={SKIP_ONBOARDING_SETUP_EVENT_ID}
              />
            )}
            <Modal.Body
              css={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { base: 'flex-start', md: 'safe center' },
                width: '100%',
              }}
            >
              <Box
                css={{
                  width: '100%',
                  maxWidth: isNewUser ? '480px' : '',
                }}
              >
                {renderQuestionField()}
                <Box css={{ display: { base: 'none', md: 'flex' }, marginTop: '400' }}>
                  <OnboardingButtons
                    currentStep={currentStep}
                    currentStepIndex={currentStepIndex}
                    isSaving={isSaving}
                    isSubmitDisabled={isSubmitDisabled || hasNothingToSave}
                    isNewUser={isNewUser}
                    onBack={handleBack}
                    onContinue={handleContinue}
                    onSkip={onSkipClick}
                  />
                </Box>
              </Box>
            </Modal.Body>
            <Modal.Footer css={{ display: { base: 'flex', md: 'none' } }}>
              <Box css={{ justifyContent: 'space-between', width: '100%' }}>
                <OnboardingButtons
                  currentStep={currentStep}
                  currentStepIndex={currentStepIndex}
                  isSaving={isSaving}
                  isSubmitDisabled={isSubmitDisabled || hasNothingToSave}
                  isNewUser={isNewUser}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  onSkip={onSkipClick}
                />
              </Box>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal>
  );
};

export default UserOnboarding;
